/**
 * Crescent — Generic content page renderer.
 * Drives /crescent/{quality|sustainability|resources|about|contact} from CMS.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, AlertTriangle, Mail, Phone, MapPin, MessageCircle, BookOpen } from "lucide-react";
import CrescentLayout from "./CrescentLayout";
import { useCrescentT, TTSPlayer } from "./CrescentI18n";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CrescentContentPage({ slug }) {
  const [raw, setRaw] = useState(null);
  const [state, setState] = useState("loading");
  const { t: page } = useCrescentT(raw);

  useEffect(() => {
    axios.get(`${API}/crescent/pages/${slug}`)
      .then((r) => { setRaw(r.data.page); setState("ok"); })
      .catch(() => setState("not-found"));
  }, [slug]);

  usePageSeo(page
    ? { title: `${page.title} — Crescent Flexi Pack`, description: (page.lede || "").slice(0, 170) }
    : { title: "Loading…", noindex: true }
  );
  useBreadcrumbSchema(page ? [
    { name: "Home", url: "/crescent" },
    { name: page.title, url: `/crescent/${slug}` },
  ] : []);

  if (state === "loading") {
    return <CrescentLayout><div className="py-24 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" size={24} /></div></CrescentLayout>;
  }
  if (state === "not-found" || !page) {
    return (
      <CrescentLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertTriangle size={32} className="mx-auto text-amber-500" />
          <h1 className="mt-3 font-heading text-3xl font-bold text-slate-900">Page not found</h1>
          <Link to="/crescent" className="mt-5 inline-flex items-center gap-2 text-emerald-700 font-semibold">
            Back home <ArrowRight size={14} />
          </Link>
        </div>
      </CrescentLayout>
    );
  }

  return (
    <CrescentLayout>
      <div data-testid={`crescent-page-${slug}`}>
        {/* Hero */}
        <section className={`relative text-white py-16 bg-cover bg-center bg-gradient-to-br ${page.fallback_gradient || 'from-emerald-500 via-emerald-600 to-teal-700'}`}
          style={page.hero_image ? { backgroundImage: `linear-gradient(135deg, rgba(16,185,129,0.72), rgba(13,148,136,0.78)), url(${page.hero_image})` } : {}}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-xs text-amber-100 mb-3">
              <Link to="/crescent" className="hover:underline">Home</Link>
              <span className="mx-2">›</span>
              <span className="text-white">{page.title}</span>
            </nav>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-tight" data-testid="content-title">{page.title}</h1>
            {page.lede && <p className="mt-4 text-lg text-white/90 max-w-3xl">{page.lede}</p>}
            {page && (
              <div className="mt-6">
                <TTSPlayer
                  text={[
                    page.title,
                    page.lede,
                    ...(page.sections || []).flatMap((s) => [s.heading, s.body]),
                  ].filter(Boolean).join(". ")}
                  label="Listen to page"
                />
              </div>
            )}
          </div>
        </section>

        {/* Stats (About / Quality / Sustainability) */}
        {page.stats?.length > 0 && (
          <section className="py-10 bg-slate-50 border-y border-slate-200">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {page.stats.map((s, i) => (
                <div key={i} data-testid={`content-stat-${i}`}>
                  <div className="font-heading font-extrabold text-emerald-700 text-3xl">{s.stat}</div>
                  <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Section-list layout */}
        {page.sections?.length > 0 && (
          <section className="py-14">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10" data-testid="content-sections">
              {page.sections.map((s, i) => (
                <div key={i}>
                  <h2 className="font-heading font-extrabold text-slate-900 text-2xl mb-3">{s.heading}</h2>
                  <p className="text-base text-slate-700 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Resources articles card grid */}
        {page.articles?.length > 0 && (
          <section className="py-14 bg-slate-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl mb-8">Knowledge library</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resources-list">
                {page.articles.map((a) => (
                  <div
                    key={a.slug}
                    data-testid={`resource-card-${a.slug}`}
                    className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-500 hover:shadow-xl transition-all"
                  >
                    <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                      <BookOpen size={14} /> {a.tag || "Article"}
                    </div>
                    <h3 className="font-heading font-bold text-slate-900 text-lg mt-3 group-hover:text-emerald-700">{a.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{a.summary}</p>
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new Event("open-crescent-chat"))}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"
                    >
                      Ask our AI <ArrowRight size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Contact detail block */}
        {slug === "contact" && (
          <section className="py-14 bg-white border-t border-slate-100">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="contact-cards">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><MapPin size={18} /></div>
                <h3 className="font-bold text-slate-900 mt-3">Head office & factory</h3>
                <p className="text-sm text-slate-600 mt-1">Alappuzha, Kerala, India</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Mail size={18} /></div>
                <h3 className="font-bold text-slate-900 mt-3">Sales enquiries</h3>
                <a href="mailto:sales@crescentflexipack.com" className="text-sm text-emerald-700 mt-1 inline-block hover:underline">sales@crescentflexipack.com</a>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Phone size={18} /></div>
                <h3 className="font-bold text-slate-900 mt-3">Phone / WhatsApp</h3>
                <p className="text-sm text-slate-600 mt-1">+91 484 255 0000</p>
              </div>
            </div>
          </section>
        )}

        {/* Shared CTA */}
        <section className="py-14 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-extrabold text-3xl">Start a conversation with our team</h2>
            <p className="mt-3 text-emerald-100">Share your product brief, or ask our AI Packaging Assistant anything about flexible packaging.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/crescent/request-quote" className="inline-flex items-center gap-2 bg-white text-emerald-800 font-bold px-6 py-3 rounded-full">
                Request a Quote <ArrowRight size={16} />
              </Link>
              <button onClick={() => window.dispatchEvent(new Event("open-crescent-chat"))} className="inline-flex items-center gap-2 bg-emerald-900/40 border border-white/30 text-white font-bold px-6 py-3 rounded-full">
                <MessageCircle size={16} /> Chat with AI
              </button>
            </div>
          </div>
        </section>
      </div>
    </CrescentLayout>
  );
}
